/**
 * Acceso a localStorage tolerante a fallos.
 *
 * En navegadores de celular (Safari en modo privado, Chrome/Brave con
 * "bloquear datos de sitios", WebViews de Facebook/Instagram) `localStorage`
 * puede lanzar una excepción tanto al leer como al escribir. Como el login
 * guardaba el token directamente con `localStorage.setItem`, esa excepción
 * caía en el `catch` del store y el usuario veía "Error al iniciar sesión"
 * aunque el backend hubiera respondido 200.
 *
 * Si el almacenamiento persistente no está disponible se usa un respaldo en
 * memoria: la sesión funciona mientras la pestaña esté abierta.
 */

const memoryStore = new Map()

let persistentAvailable = null

/** ¿Se puede realmente escribir en localStorage en este navegador? */
export const isPersistentStorageAvailable = () => {
  if (persistentAvailable !== null) return persistentAvailable

  try {
    const probe = '__storage_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    persistentAvailable = true
  } catch (error) {
    console.warn('[safeStorage] localStorage no disponible, se usa memoria:', error?.name || error)
    persistentAvailable = false
  }

  return persistentAvailable
}

export const getItem = (key) => {
  if (isPersistentStorageAvailable()) {
    try {
      return window.localStorage.getItem(key)
    } catch (error) {
      console.warn('[safeStorage] error al leer', key, error?.name || error)
    }
  }

  return memoryStore.has(key) ? memoryStore.get(key) : null
}

export const setItem = (key, value) => {
  memoryStore.set(key, value)

  if (isPersistentStorageAvailable()) {
    try {
      window.localStorage.setItem(key, value)
      return true
    } catch (error) {
      console.warn('[safeStorage] error al escribir', key, error?.name || error)
      return false
    }
  }

  return false
}

export const removeItem = (key) => {
  memoryStore.delete(key)

  if (isPersistentStorageAvailable()) {
    try {
      window.localStorage.removeItem(key)
    } catch (error) {
      console.warn('[safeStorage] error al borrar', key, error?.name || error)
    }
  }
}

/** Igual que getItem pero parseando JSON sin reventar si el valor está corrupto. */
export const getJSON = (key, fallback = null) => {
  const raw = getItem(key)
  if (!raw || raw === 'undefined') return fallback

  try {
    return JSON.parse(raw)
  } catch (error) {
    console.warn('[safeStorage] JSON inválido en', key, '- se descarta')
    removeItem(key)
    return fallback
  }
}

export default { getItem, setItem, removeItem, getJSON, isPersistentStorageAvailable }
